import { Grid, IconButton } from "@mui/material";
import { INavigationLink } from "../../../../interfaces/INavigation";
import ContentTitle from "../../../../components/Content/ContentTitle";
import useDrawer from "../../../../hooks/useDrawer";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import { useAuthContext } from "../../../../contexts/AuthContext";
import Tooltip from "../../../../components/Tooltip";
import { Add, Subtitles, FilterAlt, Print, Refresh, Search as SearchIcon } from "@mui/icons-material";
import Legend from "../../../../components/ServiceOrder/Legend";
import Filters from "../../../../components/ServiceOrder/Filters";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useServiceOrderContext } from "../../../../contexts/ServiceOrderContext";
import useSearch from "../../../../hooks/useSearch";
import IServiceOrder from "../../../../interfaces/IServiceOrder";
import useState from "../../../../hooks/useState";
import Empty from "../../../../components/Empty";
import DefaultConstants from "../../../../data/Constants";
import { ServiceStatusData } from "../../../../data/Enums";
import StringHelper from "../../../../helpers/StringHelper";
import Card from "../../../../components/ServiceOrder/Card";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import Search from "../../../../components/Search";
import useDialog from "../../../../hooks/useDialog";
import AddDialog from "../../../../components/ServiceOrder/AddDialog";
import ViewDialog from "../../../../components/ServiceOrder/ViewDialog";
import { ReportsAlias, ServiceReports } from "../../../../data/Reports";
import { IServiceOrdersReportFilters } from "../Reports/ServiceOrders/Filters";
import IReport from "../../../../interfaces/IReport";
import { useReportPreviewContext } from "../../../../contexts/ReportPreviewContext";


interface IStateValues {
    serviceOrdersOnScreen: number;
    selectedServiceOrder: IServiceOrder | undefined;
}

const ServiceOrders = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { environment } = useEnvironmentContext();
    const { enterprise, hasPermission } = useAuthContext();
    const { 
        selectedStatus,
        selectedPriority,
        selectedDepartment,
        filteredServiceOrders,
        startDateOperator, 
        startDate, 
        estimatedCompletionDateOperator, 
        estimatedCompletionDate, 
        handleRefreshServiceOrders 
    } = useServiceOrderContext();
    const { open: reportPreviewOpen, handleOpen } = useReportPreviewContext();
    
    const containerRef = useRef<HTMLDivElement | null>(null);

    const legendDrawer = useDrawer();
    const filterDrawer = useDrawer();
    
    const search = useSearch();
    const addDialog = useDialog();
    
    const state = useState<IStateValues>({
        serviceOrdersOnScreen: 100,
        selectedServiceOrder: undefined
    });


    const handleRefresh = useCallback((showLoading: boolean | undefined = undefined) => {
        const conditionsToShowLoading = [
            !reportPreviewOpen,
            state.values.selectedServiceOrder === undefined, 
            !addDialog.open, 
            !legendDrawer.open, 
            !filterDrawer.open, 
            !search.open
        ];

        showLoading = showLoading !== undefined ? showLoading : conditionsToShowLoading.every(c => c === true);
        handleRefreshServiceOrders(() => {}, showLoading);
        // eslint-disable-next-line
    }, [state.values.selectedServiceOrder, addDialog.open, legendDrawer.open, filterDrawer.open, search.open, reportPreviewOpen]);


    useEffect(() => {
        handleRefresh();
        // eslint-disable-next-line
    }, []);


    useEffect(() => {
        if (state.values.selectedServiceOrder !== undefined) {
            return;
        }

        const milliseconds = environment.serviceOrder.dataRefreshTimeInSeconds * 1000;
        const interval = setInterval(() => {
            handleRefresh();
        }, milliseconds);
            
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [handleRefresh, state.values.selectedServiceOrder]);


    useEffect(() => {
        if (containerRef.current) {
            const container = containerRef.current;
            const serviceOrdersOnScreen = state.values.serviceOrdersOnScreen;
                
            container.onscroll = () => {
                const onContainerBottom = Math.abs(container.scrollHeight - container.clientHeight - container.scrollTop) < 10;
                if (onContainerBottom && serviceOrdersOnScreen < filteredServiceOrders.length) {
                    state.update('serviceOrdersOnScreen', serviceOrdersOnScreen + 100);
                }
            }
        }
        // eslint-disable-next-line
    }, [filteredServiceOrders, state.values.serviceOrdersOnScreen, containerRef]);


    const handleOpenReportPreview = useCallback(() => {
        const report = ServiceReports.find(r => r.alias === ReportsAlias.Service.ServiceOrders) as IReport;
        const filters: IServiceOrdersReportFilters = {
            enterprise: enterprise,
            status: selectedStatus,
            priority: selectedPriority,
            department: enterprise?.departments.filter(d => selectedDepartment.includes(+d.id)) ?? [],
            responsible: [],
            room: [],
            startDateOperator: startDateOperator,
            startDate: startDate,
            estimatedCompletionDateOperator: estimatedCompletionDateOperator,
            estimatedCompletionDate: estimatedCompletionDate
        };

        handleOpen(report, filters);
        // eslint-disable-next-line
    }, [enterprise, selectedStatus, selectedPriority, selectedDepartment, startDateOperator, startDate, estimatedCompletionDateOperator, estimatedCompletionDate]);


    const serviceOrderCards = useMemo(() => {
        const allowedStatus = Object.values(ServiceStatusData)
            .filter(s => hasPermission(`Service.ServiceOrders.View.${StringHelper.FirstLetterUpperCase(s.name)}`))
            .map(s => s.key);

        let serviceOrdersAux = filteredServiceOrders.filter(r => r.statusId !== undefined && allowedStatus.includes(+r.statusId));

        if (search.value.trim().length > 0) {
            const value = StringHelper.Normalize(search.value.toUpperCase());
            serviceOrdersAux = serviceOrdersAux.filter(r => {
                const matchId = StringHelper.Normalize(r.id.toString().toUpperCase()).includes(value);
                const matchDescription = StringHelper.Normalize(r.description.toUpperCase()).includes(value);
                return matchId || matchDescription;
            });
        }
        
        return serviceOrdersAux.slice(0, state.values.serviceOrdersOnScreen).map(s => {
            return <Grid item lg={1.2} md={2.4} sm={4} xs={6} key={s.id}>
                <Card
                    prefix='#'
                    title={s.id.toString()}
                    serviceOrder={s}
                    showStatusTooltip={true}
                    key={s.id}
                    onClick={() => state.update('selectedServiceOrder', s)}
                />
            </Grid>;
        });
        // eslint-disable-next-line
    }, [filteredServiceOrders, state.values, search.value]);
    
    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle 
                        name={translate(linkProps.name)} 
                        description={enterprise?.name}
                        applyTranslation={false}
                    />
                </Grid>

                <Grid item className='grid-tools'>
                    <Tooltip title={translate('add')}>
                        <IconButton onClick={() => addDialog.handleOpen()}>
                            <Add />
                        </IconButton>
                    </Tooltip>

                    {
                        hasPermission(ReportsAlias.Service.ServiceOrders) && (
                            <Tooltip title={translate('print')}>
                                <IconButton onClick={handleOpenReportPreview}>
                                    <Print />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                    
                    <Tooltip title={translate('search')}>
                        <IconButton onClick={() => search.handleOpen()}>
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('legend')}>
                        <IconButton onClick={() => legendDrawer.handleOpen()}>
                            <Subtitles />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filterDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={() => handleRefresh(true)}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            {
                serviceOrderCards.length > 0 ? (
                    <Grid 
                        container 
                        className='grid-content overflow-enabled justifycontent-flexstart aligncontent-flexstart' 
                        rowSpacing={DefaultConstants.gridRowSpacing} 
                        columnSpacing={DefaultConstants.gridColumnSpacing}
                        ref={containerRef}
                    >
                        {serviceOrderCards}
                    </Grid>
                ) : (
                    <Grid container className='grid-content'>
                        <Grid item xs={12} className='paper-content display-flex alignitems-center justifycontent-center background-transparent boxshadow-none'>
                            <Empty text='noServiceOrdersFound' className="backgroundcolor-paper boxshadow-default" />
                        </Grid>
                    </Grid>
                )
            }

            <AddDialog
                open={addDialog.open}
                handleClose={() => addDialog.handleClose()}
                handleListRefresh={() => handleRefresh(true)}
            />

            <Search
                open={search.open}
                handleClose={() => search.handleClose()}
                value={search.value}
                handleChange={(e) => search.handleChangeValue(e.currentTarget.value)}
            />

            <Legend
                open={legendDrawer.open}
                handleClose={() => legendDrawer.handleClose()}
            />

            <Filters
                open={filterDrawer.open}
                handleClose={() => filterDrawer.handleClose()}
            />

            <ViewDialog
                open={state.values.selectedServiceOrder !== undefined}
                handleClose={() => {
                    state.update('selectedServiceOrder', undefined);
                    handleRefresh(true);
                }}
                serviceOrderId={state.values.selectedServiceOrder?.id}
            />
        </>
    );
}

export default ServiceOrders;